import React from 'react';
import { Box } from '@mui/material';
import { FlexyFormLabel } from '@europrocurement/flexy-components';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import {
    GoogleMapAutocomplete,
    GoogleMapAutocompleteProps,
    SirenAutocompleteProps,
} from '@europrocurement/l2d-modules';
import SirenAutocomplete from '@europrocurement/l2d-modules/modules/PrescriberClient/components/AddPrescriberClient/SirenAutocomplete/SirenAutocomplete';
import { Tiers } from '@europrocurement/l2d-modules/providers/TiersProvider';

type AddressFormatted = Parameters<GoogleMapAutocompleteProps['onChange']>[0];
type CompanyType = Parameters<SirenAutocompleteProps['onSelectItem']>[0];

export type PrescriberInfoFormValues = {
    raisonSociale: string;
    codeComptable: string;
    adresse: string;
    adresseComplement: string;
    codePostal: string;
    ville: string;
    codePays: string;
    fullAddress: string;
    siren: string;
    tvaIntracommunautaire: string;
    regimeTva: string;
};

export type PrescriberInfoCardEditProps = {
    tiers: Tiers;
    onSubmit: (values: PrescriberInfoFormValues) => void;
};

const formStructure: FormStructure[] = [
    {
        type: 'text',
        name: 'raisonSociale',
        inputlabel: 'Raison sociale',
        fullWidth: true,
        lg: 12,
        md: 12,
        sm: 12,
    },
    {
        type: 'text',
        name: 'codeComptable',
        inputlabel: 'Code client',
        fullWidth: true,
        lg: 12,
        md: 12,
        sm: 12,
    },
    {
        type: 'customItem',
        name: 'fullAddress',
        lg: 12,
        md: 12,
        sm: 12,
        renderField: (formContext) => {
            const currentValue = (formContext.getValue('fullAddress') as string) ?? '';

            const handleAddressChange = (formattedAddress: AddressFormatted) => {
                formContext.setValue('adresse', formattedAddress.address);
                formContext.setValue('adresseComplement', formattedAddress.addressComplement ?? '');
                formContext.setValue('codePostal', formattedAddress.postalCode);
                formContext.setValue('ville', formattedAddress.city);
                formContext.setValue('codePays', formattedAddress.countryCode);
                formContext.setValue(
                    'fullAddress',
                    [
                        formattedAddress.address,
                        formattedAddress.addressComplement,
                        formattedAddress.postalCode,
                        formattedAddress.city,
                    ]
                        .filter(Boolean)
                        .join(', '),
                );
            };

            return (
                <Box>
                    <FlexyFormLabel>Adresse</FlexyFormLabel>
                    <GoogleMapAutocomplete
                        inputValue={currentValue}
                        placeholder="Rechercher une adresse..."
                        onChange={handleAddressChange}
                        onKeyUp={() => {}}
                        isFrenchAddress
                    />
                </Box>
            );
        },
    },
    {
        type: 'customItem',
        name: 'siren',
        lg: 12,
        md: 12,
        sm: 12,
        renderField: (formContext) => {
            const currentValue = (formContext.getValue('siren') as string) ?? '';

            const handleSirenChange = (siren: string) => {
                formContext.setValue('siren', siren);
            };

            const handleSelectCompany = (company: CompanyType) => {
                formContext.setValue('siren', company.siren ?? '');
            };

            return (
                <Box>
                    <FlexyFormLabel>SIREN</FlexyFormLabel>
                    <SirenAutocomplete
                        defaultValue={currentValue}
                        placeholder="Rechercher par SIREN ou SIRET..."
                        onChange={handleSirenChange}
                        onSelectItem={handleSelectCompany}
                    />
                </Box>
            );
        },
    },
    {
        type: 'text',
        name: 'tvaIntracommunautaire',
        inputlabel: 'N° TVA intracommunautaire',
        fullWidth: true,
        lg: 12,
        md: 12,
        sm: 12,
    },
    {
        type: 'text',
        name: 'regimeTva',
        inputlabel: 'Régime TVA',
        fullWidth: true,
        lg: 12,
        md: 12,
        sm: 12,
    },
];

const PrescriberInfoCardEdit: React.FunctionComponent<PrescriberInfoCardEditProps> = function ({
    tiers,
    onSubmit,
}) {
    const formObject: PrescriberInfoFormValues = {
        raisonSociale: tiers.raisonSociale ?? '',
        codeComptable: tiers.codeComptable ?? '',
        adresse: tiers.adresse.adresse1 ?? '',
        adresseComplement: tiers.adresse.adresse2 ?? '',
        codePostal: tiers.adresse.codePostal ?? '',
        ville: tiers.adresse.ville ?? '',
        codePays: tiers.adresse.codePays ?? '',
        fullAddress: tiers.adresse.fullAddress ?? '',
        siren: tiers.siren ?? '',
        tvaIntracommunautaire: tiers.tvaIntracommunautaire ?? '',
        regimeTva: tiers.regimeTva ?? '',
    };

    return (
        <Box sx={{ padding: '0 32px 24px' }}>
            <FlexyForm
                formObject={formObject}
                formStructure={formStructure}
                onSubmit={onSubmit}
            />
        </Box>
    );
};

export default PrescriberInfoCardEdit;
